import PropTypes from 'prop-types';
import { Button, Icon, ListboxMultiselect } from '@evertrue/et-components';
import UserSelector from 'apps/users/components/user-selector';
import SuccessMetricsSection from './success-metrics/success-metrics-section';
import ExitConditionsSection from './exit-conditions/exit-conditions-section';
import AudienceSection from './audience-section';

/**
 * Step 1 of the cadence creation wizard — "Configure Cadence".
 * Renders success metrics, optional recipient selection, and collapsible
 * advanced settings (exit conditions, sender details, sharing, proposals,
 * blueprint link, tags, and enrollment limits).
 *
 * All state lives in the parent CreateCadencePage and is passed down as props.
 */
const CadenceSetupStep = ({
  // Config flags
  isDebugMode,
  isBlueprint,
  isProspectPlan,
  typeLabel,
  readonly,
  canDelete,
  orgDomain,

  // Success Metrics
  successMetric,
  setSuccessMetric,
  additionalMetrics,
  setAdditionalMetrics,
  interactionTypes,
  savedSearches,
  portfolioTeams,

  // Recipients
  isRecipientsOpen,
  setIsRecipientsOpen,
  existingAudiences,
  audiencesLoaded,
  onDisconnectAudience,
  onDisconnectAllManual,
  selectionMethod,
  setSelectionMethod,
  selectedList,
  setSelectedList,
  selectedSavedSearch,
  setSelectedSavedSearch,
  selectedContacts,
  setSelectedContacts,
  contactLists,
  getRecipientCount,

  // Advanced Settings — Exit Conditions
  isAdvancedOpen,
  setIsAdvancedOpen,
  isExitConditionsOpen,
  setIsExitConditionsOpen,
  removalRules,
  setRemovalRules,
  cadenceOptions,

  // Sender Details
  isSenderDetailsOpen,
  setIsSenderDetailsOpen,
  senderEmailPrefix,
  setSenderEmailPrefix,
  senderName,
  setSenderName,
  replyToEmail,
  setReplyToEmail,

  // Sharing
  isSharingOpen,
  setIsSharingOpen,
  sharedUsers,
  setSharedUsers,
  pendingShareUsers,
  setPendingShareUsers,
  pendingSharePermission,
  setPendingSharePermission,

  // Proposals
  isProposalsOpen,
  setIsProposalsOpen,
  linkedProposals,
  setLinkedProposals,
  proposals,
  onProposalSearch,

  // Blueprint
  isBlueprintOpen,
  setIsBlueprintOpen,
  cadenceTemplate,
  setCadenceTemplate,
  templateSearch,
  setTemplateSearch,
  templateDropdownOpen,
  setTemplateDropdownOpen,
  cadenceShells,

  // Tags
  isTagsOpen,
  setIsTagsOpen,
  campaignTags,
  tagInput,
  setTagInput,
  tagSuggestions,
  handleAddTag,
  handleRemoveTag,
  handleSelectTagSuggestion,

  // Enrollment
  isEnrollmentLimitOpen,
  setIsEnrollmentLimitOpen,
  singleEnrollmentOnly,
  setSingleEnrollmentOnly,
  enrollmentLocked,
  setEnrollmentLocked,
}) => {
  return (
    <div className="create-cadence-configure">
      {/* Success Metrics - REQUIRED */}
      <div className="create-cadence-configure--card">
        <div className="create-cadence-configure--card-header">
          <div className="create-cadence-configure--card-header-left">
            <Icon icon="trending-up" className="create-cadence-configure--card-icon" />
            <div>
              <h3 className="create-cadence-configure--card-title">Success Metric</h3>
              <p className="create-cadence-configure--card-subtitle">
                Define how you&apos;ll measure whether this cadence is successful
              </p>
            </div>
          </div>
          <span className="create-cadence-configure--badge create-cadence-configure--badge-required">REQUIRED</span>
        </div>
        <div className="create-cadence-configure--card-body">
          <SuccessMetricsSection
            metric={successMetric}
            metrics={
              isDebugMode ? (successMetric ? [successMetric, ...additionalMetrics] : additionalMetrics) : undefined
            }
            onChange={setSuccessMetric}
            onChangeMultiple={
              isDebugMode
                ? (newMetrics) => {
                    if (newMetrics.length > 0) {
                      setSuccessMetric(newMetrics[0]);
                      setAdditionalMetrics(newMetrics.slice(1));
                    } else {
                      setSuccessMetric(null);
                      setAdditionalMetrics([]);
                    }
                  }
                : undefined
            }
            interactionTypes={interactionTypes}
            savedSearches={savedSearches}
            portfolioTeams={portfolioTeams}
          />
        </div>
      </div>

      {/* Select Recipients / Contact - OPTIONAL, collapsible (hidden for blueprints) */}
      {!isBlueprint && (
        <div className="create-cadence-configure--card">
          <button
            type="button"
            className="create-cadence-configure--card-header create-cadence-configure--card-header-clickable"
            onClick={() => setIsRecipientsOpen(!isRecipientsOpen)}
          >
            <div className="create-cadence-configure--card-header-left">
              <Icon icon={isProspectPlan ? 'person' : 'group'} className="create-cadence-configure--card-icon" />
              <div>
                <h3 className="create-cadence-configure--card-title">
                  {isProspectPlan ? 'Select Contact' : 'Select Recipients'}
                </h3>
                <p className="create-cadence-configure--card-subtitle">
                  {isProspectPlan
                    ? 'Choose the individual this prospect plan is for'
                    : 'Choose who will receive this cadence. New recipients will be enrolled when you save.'}
                </p>
              </div>
            </div>
            <div className="create-cadence-configure--advanced-right">
              <span className="create-cadence-configure--badge create-cadence-configure--badge-optional">OPTIONAL</span>
              <Icon
                icon={isRecipientsOpen ? 'chevron-up' : 'chevron-down'}
                className="create-cadence-configure--card-toggle-icon"
              />
            </div>
          </button>

          {isRecipientsOpen && (
            <div className="create-cadence-configure--card-body">
              <AudienceSection
                isProspectPlan={isProspectPlan}
                existingAudiences={existingAudiences}
                audiencesLoaded={audiencesLoaded}
                contactLists={contactLists}
                savedSearches={savedSearches}
                onDisconnect={onDisconnectAudience}
                onDisconnectAllManual={onDisconnectAllManual}
                selectionMethod={selectionMethod}
                setSelectionMethod={setSelectionMethod}
                selectedList={selectedList}
                setSelectedList={setSelectedList}
                selectedSavedSearch={selectedSavedSearch}
                setSelectedSavedSearch={setSelectedSavedSearch}
                selectedContacts={selectedContacts}
                setSelectedContacts={setSelectedContacts}
                readonly={readonly}
                canDelete={canDelete}
              />
            </div>
          )}
        </div>
      )}

      {/* Advanced Settings - OPTIONAL, collapsible */}
      <div className="create-cadence-configure--card">
        <button
          type="button"
          className="create-cadence-configure--card-header create-cadence-configure--card-header-clickable"
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
        >
          <div className="create-cadence-configure--card-header-left">
            <Icon icon="settings" className="create-cadence-configure--card-icon" />
            <div>
              <h3 className="create-cadence-configure--card-title">Advanced Settings</h3>
              <p className="create-cadence-configure--card-subtitle">
                Exit conditions, sharing, blueprints, proposals, and tags
              </p>
            </div>
          </div>
          <div className="create-cadence-configure--advanced-right">
            <span className="create-cadence-configure--badge create-cadence-configure--badge-optional">OPTIONAL</span>
            <Icon
              icon={isAdvancedOpen ? 'chevron-up' : 'chevron-down'}
              className="create-cadence-configure--card-toggle-icon"
            />
          </div>
        </button>

        {isAdvancedOpen && (
          <div className="create-cadence-configure--card-body create-cadence-configure--advanced-body">
            {/* Exit Conditions (Removal Rules) — collapsible */}
            <div className="create-cadence-configure--subsection">
              <button
                type="button"
                className="create-cadence-configure--subsection-header"
                onClick={() => setIsExitConditionsOpen(!isExitConditionsOpen)}
              >
                <span className="create-cadence-configure--subsection-title">Exit Conditions</span>
                <Icon
                  icon={isExitConditionsOpen ? 'chevron-up' : 'chevron-down'}
                  className="create-cadence-configure--card-toggle-icon"
                />
              </button>
              {isExitConditionsOpen && (
                <div className="create-cadence-configure--subsection-body">
                  <ExitConditionsSection
                    conditions={removalRules}
                    onChange={setRemovalRules}
                    portfolioTeams={portfolioTeams}
                    cadences={cadenceOptions}
                    savedSearches={savedSearches}
                    contactLists={contactLists}
                  />
                </div>
              )}
            </div>

            <div className="create-cadence-configure--divider" />

            {/* Sender Details — collapsible */}
            <div className="create-cadence-configure--subsection">
              <button
                type="button"
                className="create-cadence-configure--subsection-header"
                onClick={() => setIsSenderDetailsOpen(!isSenderDetailsOpen)}
              >
                <span className="create-cadence-configure--subsection-title">Sender Details</span>
                <Icon
                  icon={isSenderDetailsOpen ? 'chevron-up' : 'chevron-down'}
                  className="create-cadence-configure--card-toggle-icon"
                />
              </button>
              {isSenderDetailsOpen && (
                <div className="create-cadence-configure--subsection-body">
                  <p className="create-cadence-configure--section-desc">Configure how emails appear to recipients</p>

                  {/* From Email — split input with locked domain */}
                  <div className="create-cadence-configure--field-group">
                    <label className="create-cadence-configure--field-label">From Email</label>
                    <div className="create-cadence-configure--split-email">
                      <input
                        type="text"
                        className="create-cadence-configure--input create-cadence-configure--split-email-prefix"
                        value={senderEmailPrefix}
                        onChange={(e) => setSenderEmailPrefix(e.target.value)}
                        placeholder="outreach"
                      />
                      <span className="create-cadence-configure--split-email-at">@</span>
                      <span className="create-cadence-configure--split-email-domain">{orgDomain}</span>
                    </div>
                    {senderEmailPrefix.includes('@') ? (
                      <span className="create-cadence-configure--field-error">
                        Don&apos;t include @ or a domain here — enter only the part before the @. Your sending
                        domain is fixed and shown above.
                      </span>
                    ) : (
                      <span className="create-cadence-configure--field-hint">
                        Enter only the part before the @ — your sending domain is fixed and shown above.
                      </span>
                    )}
                  </div>

                  {/* Sender Name */}
                  <div className="create-cadence-configure--field-group">
                    <label className="create-cadence-configure--field-label">Sender Name</label>
                    <input
                      type="text"
                      className="create-cadence-configure--input"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder="Your Name"
                    />
                    <span className="create-cadence-configure--field-hint">
                      This is who it will look like sent the email. Defaults to your name.
                    </span>
                  </div>

                  {/* Reply-To Email */}
                  <div className="create-cadence-configure--field-group">
                    <label className="create-cadence-configure--field-label">Reply-To Email</label>
                    <input
                      type="text"
                      className="create-cadence-configure--input"
                      value={replyToEmail}
                      onChange={(e) => setReplyToEmail(e.target.value)}
                      placeholder="you@example.com"
                    />
                    <span className="create-cadence-configure--field-hint">
                      Who will receive replies. Defaults to your email. Separate multiple addresses with commas.
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="create-cadence-configure--divider" />

            {/* Sharing — collapsible, owner-only (canDelete gates share/archive/publish) */}
            {canDelete && (
              <div className="create-cadence-configure--subsection">
                <button
                  type="button"
                  className="create-cadence-configure--subsection-header"
                  onClick={() => setIsSharingOpen(!isSharingOpen)}
                >
                  <span className="create-cadence-configure--subsection-title">Sharing</span>
                  <Icon
                    icon={isSharingOpen ? 'chevron-up' : 'chevron-down'}
                    className="create-cadence-configure--card-toggle-icon"
                  />
                </button>
                {isSharingOpen && (
                  <div className="create-cadence-configure--subsection-body">
                    <p className="create-cadence-configure--section-desc">
                      Control who can access this {typeLabel.toLowerCase()}
                    </p>

                    {/* Add people */}
                    <div className="create-cadence-configure--sharing-add">
                      <span className="create-cadence-configure--sharing-add-label">Add people</span>
                      <div className="create-cadence-configure--sharing-add-row">
                        <div className="create-cadence-configure--sharing-add-picker">
                          <UserSelector selected={pendingShareUsers} onChange={setPendingShareUsers} />
                        </div>
                        <select
                          className="create-cadence-configure--select create-cadence-configure--select-small"
                          value={pendingSharePermission}
                          onChange={(e) => setPendingSharePermission(e.target.value)}
                        >
                          <option value="view">Can view</option>
                          <option value="edit">Can edit</option>
                        </select>
                        <Button
                          type="secondary"
                          disabled={pendingShareUsers.length === 0}
                          onClick={() => {
                            const newUsers = pendingShareUsers
                              .map((u) => {
                                const userId = typeof u === 'object' ? u.value : u;
                                const label = typeof u === 'object' ? u.label : `User ${u}`;
                                return { userId, label, permission: pendingSharePermission };
                              })
                              .filter((u) => !sharedUsers.some((s) => String(s.userId) === String(u.userId)));
                            setSharedUsers([...sharedUsers, ...newUsers]);
                            setPendingShareUsers([]);
                          }}
                        >
                          Add
                        </Button>
                      </div>
                    </div>

                    {/* People with access */}
                    {sharedUsers.length > 0 && (
                      <div className="create-cadence-configure--sharing-people">
                        <span className="create-cadence-configure--sharing-people-label">People with access</span>
                        {sharedUsers.map((share) => (
                          <div key={share.userId} className="create-cadence-configure--sharing-person-row">
                            <span className="create-cadence-configure--sharing-person-name">
                              {share.label || `User ${share.userId}`}
                            </span>
                            <select
                              className="create-cadence-configure--select create-cadence-configure--select-small"
                              value={share.permission}
                              onChange={(e) =>
                                setSharedUsers(
                                  sharedUsers.map((s) =>
                                    String(s.userId) === String(share.userId) ? { ...s, permission: e.target.value } : s
                                  )
                                )
                              }
                            >
                              <option value="view">Can view</option>
                              <option value="edit">Can edit</option>
                            </select>
                            <button
                              type="button"
                              className="create-cadence-configure--sharing-remove-btn"
                              title="Remove access"
                              onClick={() =>
                                setSharedUsers(sharedUsers.filter((s) => String(s.userId) !== String(share.userId)))
                              }
                            >
                              <Icon icon="close" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="create-cadence-configure--divider" />

            {/* Linked Proposals — collapsible */}
            <div className="create-cadence-configure--subsection">
              <button
                type="button"
                className="create-cadence-configure--subsection-header"
                onClick={() => setIsProposalsOpen(!isProposalsOpen)}
              >
                <span className="create-cadence-configure--subsection-title">Linked Proposals</span>
                <Icon
                  icon={isProposalsOpen ? 'chevron-up' : 'chevron-down'}
                  className="create-cadence-configure--card-toggle-icon"
                />
              </button>
              {isProposalsOpen && (
                <div className="create-cadence-configure--subsection-body">
                  <p className="create-cadence-configure--section-desc">
                    Connect this {typeLabel.toLowerCase()} to proposals you&apos;re working to close
                  </p>
                  <ListboxMultiselect
                    size="medium"
                    emptyValue="Search and select proposals..."
                    label="Search and select proposals..."
                    options={proposals}
                    onChange={setLinkedProposals}
                    values={linkedProposals}
                    searchable={true}
                    onSearch={onProposalSearch}
                    showInfoBar={true}
                  />
                  {linkedProposals.length > 0 && (
                    <div className="create-cadence-configure--tags">
                      {linkedProposals.map((p) => (
                        <span key={p.value} className="create-cadence-configure--tag">
                          {p.label}
                          <button
                            type="button"
                            onClick={() => setLinkedProposals(linkedProposals.filter((lp) => lp.value !== p.value))}
                          >
                            <Icon icon="close" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {!isBlueprint && <div className="create-cadence-configure--divider" />}

            {/* Cadence Blueprint — collapsible, hidden when creating/editing a blueprint */}
            {!isBlueprint && (
              <div className="create-cadence-configure--subsection">
                <button
                  type="button"
                  className="create-cadence-configure--subsection-header"
                  onClick={() => setIsBlueprintOpen(!isBlueprintOpen)}
                >
                  <span className="create-cadence-configure--subsection-title">{typeLabel} Blueprint</span>
                  <Icon
                    icon={isBlueprintOpen ? 'chevron-up' : 'chevron-down'}
                    className="create-cadence-configure--card-toggle-icon"
                  />
                </button>
                {isBlueprintOpen && (
                  <div className="create-cadence-configure--subsection-body">
                    <p className="create-cadence-configure--section-desc">
                      Link to a Cadence Blueprint for reporting and analytics
                    </p>
                    <div className="create-cadence-configure--template-search">
                      <div className="create-cadence-configure--template-input-row">
                        <input
                          type="text"
                          className="create-cadence-configure--input"
                          placeholder="Search cadence blueprints..."
                          value={templateSearch}
                          onChange={(e) => {
                            setTemplateSearch(e.target.value);
                            setTemplateDropdownOpen(true);
                          }}
                          onFocus={() => setTemplateDropdownOpen(true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setTemplateDropdownOpen(false);
                          }}
                        />
                        {templateDropdownOpen && (
                          <button
                            type="button"
                            className="create-cadence-configure--template-close-btn"
                            onClick={() => setTemplateDropdownOpen(false)}
                            aria-label="Close blueprint dropdown"
                          >
                            <Icon icon="close" />
                          </button>
                        )}
                      </div>
                      {templateDropdownOpen && (
                        <div className="create-cadence-configure--template-dropdown">
                          {cadenceShells
                            .filter(
                              (t) =>
                                t.name?.toLowerCase().includes(templateSearch.toLowerCase()) &&
                                (t.cadenceType === 'cadence' || t.cadenceType === 'prospect_plan' || !t.cadenceType)
                            )
                            .map((t) => (
                              <button
                                key={t.id}
                                type="button"
                                className={`create-cadence-configure--template-dropdown-item ${cadenceTemplate === String(t.id) ? 'is-selected' : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setCadenceTemplate(String(t.id));
                                  setTemplateSearch(t.name);
                                  setTemplateDropdownOpen(false);
                                }}
                              >
                                {t.name}
                              </button>
                            ))}
                          {cadenceShells.filter((t) => t.name?.toLowerCase().includes(templateSearch.toLowerCase()))
                            .length === 0 && (
                            <div className="create-cadence-configure--template-dropdown-empty">
                              No cadence blueprints found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {cadenceTemplate && (
                      <button
                        type="button"
                        className="create-cadence-configure--unlink-btn"
                        onClick={() => {
                          setCadenceTemplate('');
                          setTemplateSearch('');
                        }}
                      >
                        Unlink from blueprint
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="create-cadence-configure--divider" />

            {/* Cadence Tags — collapsible */}
            <div className="create-cadence-configure--subsection">
              <button
                type="button"
                className="create-cadence-configure--subsection-header"
                onClick={() => setIsTagsOpen(!isTagsOpen)}
              >
                <span className="create-cadence-configure--subsection-title">Cadence Tags</span>
                <Icon
                  icon={isTagsOpen ? 'chevron-up' : 'chevron-down'}
                  className="create-cadence-configure--card-toggle-icon"
                />
              </button>
              {isTagsOpen && (
                <div className="create-cadence-configure--subsection-body">
                  <p className="create-cadence-configure--section-desc">
                    Add tags to help organize and find your {isProspectPlan ? 'prospect plans' : 'cadences'}
                  </p>
                  <div className="create-cadence-configure--add-row">
                    <div className="create-cadence-configure--tag-search">
                      <input
                        type="text"
                        className="create-cadence-configure--input"
                        placeholder="Add a tag..."
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                      />
                      {tagSuggestions.length > 0 && tagInput.length > 0 && (
                        <div className="create-cadence-configure--tag-dropdown">
                          {tagSuggestions
                            .filter((s) => !campaignTags.map((t) => t.toLowerCase()).includes(s.toLowerCase()))
                            .map((suggestion) => (
                              <button
                                key={suggestion}
                                type="button"
                                className="create-cadence-configure--tag-dropdown-item"
                                onMouseDown={(e) => {
                                  // Prevent input blur before click registers
                                  e.preventDefault();
                                  handleSelectTagSuggestion(suggestion);
                                }}
                              >
                                {suggestion}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                    <Button type="action" onClick={handleAddTag} disabled={!tagInput.trim()}>
                      Add
                    </Button>
                  </div>
                  {campaignTags.length > 0 && (
                    <div className="create-cadence-configure--tags">
                      {campaignTags.map((tag) => (
                        <span key={tag} className="create-cadence-configure--tag">
                          {tag}
                          <button type="button" onClick={() => handleRemoveTag(tag)}>
                            <Icon icon="close" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Enrollment Limit — collapsible, closed by default */}
            <div className="create-cadence-configure--divider" />
            <div className="create-cadence-configure--subsection">
              <button
                type="button"
                className="create-cadence-configure--subsection-header"
                onClick={() => setIsEnrollmentLimitOpen(!isEnrollmentLimitOpen)}
              >
                <span className="create-cadence-configure--subsection-title">Enrollment Limit</span>
                <Icon
                  icon={isEnrollmentLimitOpen ? 'chevron-up' : 'chevron-down'}
                  className="create-cadence-configure--card-toggle-icon"
                />
              </button>
              {isEnrollmentLimitOpen && (
                <div className="create-cadence-configure--subsection-body">
                  <label className="create-cadence-configure--checkbox-row">
                    <input
                      type="checkbox"
                      checked={singleEnrollmentOnly}
                      onChange={(e) => setSingleEnrollmentOnly(e.target.checked)}
                      className="create-cadence-configure--checkbox"
                    />
                    <span>Only allow each contact to go through this cadence a maximum of one time</span>
                  </label>
                  {singleEnrollmentOnly && (
                    <p className="create-cadence-configure--section-desc" style={{ marginTop: 8 }}>
                      Contacts who have already completed or exited this cadence will not be re-enrolled, even if they
                      re-qualify through a saved search or list sync.
                    </p>
                  )}
                  {/* Enrollment lock — hidden for prospect plans (always locked by design) */}
                  {!isProspectPlan && (
                    <>
                      <label className="create-cadence-configure--checkbox-row" style={{ marginTop: 12 }}>
                        <input
                          type="checkbox"
                          checked={enrollmentLocked}
                          onChange={(e) => setEnrollmentLocked(e.target.checked)}
                          className="create-cadence-configure--checkbox"
                        />
                        <span>Lock enrollment (no new manual enrollments)</span>
                      </label>
                      {enrollmentLocked && (
                        <p className="create-cadence-configure--section-desc" style={{ marginTop: 8 }}>
                          Users will not be able to manually add contacts to this cadence. Saved search auto-sync will
                          continue to enroll contacts automatically.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

CadenceSetupStep.propTypes = {
  // Config flags
  isDebugMode: PropTypes.bool,
  isBlueprint: PropTypes.bool,
  isProspectPlan: PropTypes.bool,
  typeLabel: PropTypes.string,
  readonly: PropTypes.bool,
  canDelete: PropTypes.bool,
  orgDomain: PropTypes.string,

  // Success Metrics
  successMetric: PropTypes.object,
  setSuccessMetric: PropTypes.func.isRequired,
  additionalMetrics: PropTypes.array,
  setAdditionalMetrics: PropTypes.func.isRequired,
  interactionTypes: PropTypes.array,
  savedSearches: PropTypes.array,
  portfolioTeams: PropTypes.object,

  // Recipients
  isRecipientsOpen: PropTypes.bool,
  setIsRecipientsOpen: PropTypes.func.isRequired,
  existingAudiences: PropTypes.array,
  audiencesLoaded: PropTypes.bool,
  onDisconnectAudience: PropTypes.func.isRequired,
  onDisconnectAllManual: PropTypes.func.isRequired,
  selectionMethod: PropTypes.object,
  setSelectionMethod: PropTypes.func.isRequired,
  selectedList: PropTypes.object,
  setSelectedList: PropTypes.func.isRequired,
  selectedSavedSearch: PropTypes.object,
  setSelectedSavedSearch: PropTypes.func.isRequired,
  selectedContacts: PropTypes.array,
  setSelectedContacts: PropTypes.func.isRequired,
  contactLists: PropTypes.array,
  getRecipientCount: PropTypes.func.isRequired,

  // Advanced Settings — Exit Conditions
  isAdvancedOpen: PropTypes.bool,
  setIsAdvancedOpen: PropTypes.func.isRequired,
  isExitConditionsOpen: PropTypes.bool,
  setIsExitConditionsOpen: PropTypes.func.isRequired,
  removalRules: PropTypes.array,
  setRemovalRules: PropTypes.func.isRequired,
  cadenceOptions: PropTypes.array,

  // Sender Details
  isSenderDetailsOpen: PropTypes.bool,
  setIsSenderDetailsOpen: PropTypes.func.isRequired,
  senderEmailPrefix: PropTypes.string,
  setSenderEmailPrefix: PropTypes.func.isRequired,
  senderName: PropTypes.string,
  setSenderName: PropTypes.func.isRequired,
  replyToEmail: PropTypes.string,
  setReplyToEmail: PropTypes.func.isRequired,

  // Sharing
  isSharingOpen: PropTypes.bool,
  setIsSharingOpen: PropTypes.func.isRequired,
  sharedUsers: PropTypes.array,
  setSharedUsers: PropTypes.func.isRequired,
  pendingShareUsers: PropTypes.array,
  setPendingShareUsers: PropTypes.func.isRequired,
  pendingSharePermission: PropTypes.string,
  setPendingSharePermission: PropTypes.func.isRequired,

  // Proposals
  isProposalsOpen: PropTypes.bool,
  setIsProposalsOpen: PropTypes.func.isRequired,
  linkedProposals: PropTypes.array,
  setLinkedProposals: PropTypes.func.isRequired,
  proposals: PropTypes.array,
  onProposalSearch: PropTypes.func,

  // Blueprint
  isBlueprintOpen: PropTypes.bool,
  setIsBlueprintOpen: PropTypes.func.isRequired,
  cadenceTemplate: PropTypes.string,
  setCadenceTemplate: PropTypes.func.isRequired,
  templateSearch: PropTypes.string,
  setTemplateSearch: PropTypes.func.isRequired,
  templateDropdownOpen: PropTypes.bool,
  setTemplateDropdownOpen: PropTypes.func.isRequired,
  cadenceShells: PropTypes.array,

  // Tags
  isTagsOpen: PropTypes.bool,
  setIsTagsOpen: PropTypes.func.isRequired,
  campaignTags: PropTypes.array,
  tagInput: PropTypes.string,
  setTagInput: PropTypes.func.isRequired,
  tagSuggestions: PropTypes.array,
  handleAddTag: PropTypes.func.isRequired,
  handleRemoveTag: PropTypes.func.isRequired,
  handleSelectTagSuggestion: PropTypes.func.isRequired,

  // Enrollment
  isEnrollmentLimitOpen: PropTypes.bool,
  setIsEnrollmentLimitOpen: PropTypes.func.isRequired,
  singleEnrollmentOnly: PropTypes.bool,
  setSingleEnrollmentOnly: PropTypes.func.isRequired,
  enrollmentLocked: PropTypes.bool,
  setEnrollmentLocked: PropTypes.func.isRequired,
};

export default CadenceSetupStep;
